import { DefaultRootState } from 'react-redux';

import { Permissions } from 'utils/constants/permissions';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

type IsMultiCabinet = boolean;
type IsLoad = boolean;

export enum AccountStatus {
    Active = 'ACTIVE',
    ReadOnly = 'READ_ONLY',
}

export interface Account {
    id: number;
    title: string;
}

export enum AccountType {
    Government = 'GOVERNMENT',
    Common = 'COMMON',
}

export interface CurrentAccount {
    id: number | null;
    status: AccountStatus;
    expirationDate: string | null;
    type: AccountType;
    canExplicitlyChoseCampaignType: boolean | null;
    dynamicPriceEnabled: boolean | null;
}

export enum SessionType {
    Typical = 'typical',
    Sudo = 'sudo',
}

export enum UserType {
    Employer = 'EMPLOYER',
    Admin = 'ADMIN',
    SuperAdmin = 'SUPER_ADMIN',
    Moderator = 'MODERATOR',
}

export interface User {
    id: number | null;
    name: string;
    userType: UserType;
    email: string;
    employerId: number | null;
    employerName: string | null;
    employerManagerId: string | null;
    currentAccount: CurrentAccount;
    accounts: Account[];
}

export interface UserInfoDto {
    user: User;
    actualUser: User;
}

export enum UserFilterType {
    Empty = 'EMPTY',
    Any = 'ANY',
    AccountId = 'ACCOUNT_ID',
    UserId = 'USER_ID',
    EmployerId = 'EMPLOYER_ID',
    HHId = 'HH_ID',
    Name = 'NAME',
    Email = 'EMAIL',
    CampaignId = 'CAMPAIGN_ID',
    BannerId = 'BANNER_ID',
}

export interface LoginData {
    isNewEmployer?: boolean;
    isNewManager?: boolean;
}

// ----------------------- actions -------------------------------------
const SET_USER = 'SET_USER';
const SET_LOAD_USER = 'SET_LOAD_USER';
const SET_SESSION_TYPE = 'SET_SESSION_TYPE';
const SET_USER_PERMISSIONS = 'SET_USER_PERMISSIONS';
const SET_LOGIN_DATA = 'SET_LOGIN_DATA';

interface PayloadTypes {
    [SET_USER]: User | null;
    [SET_LOAD_USER]: IsLoad;
    [SET_SESSION_TYPE]: SessionType;
    [SET_USER_PERMISSIONS]: Permissions[];
    [SET_LOGIN_DATA]: LoginData;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setUser = ActionCreator(SET_USER);
export const setLoadUser = ActionCreator(SET_LOAD_USER);
export const setSessionType = ActionCreator(SET_SESSION_TYPE);
export const setUserPermissions = ActionCreator(SET_USER_PERMISSIONS);
export const setLoginData = ActionCreator(SET_LOGIN_DATA);
// ----------------------- /actions -------------------------------------

// ----------------------- selectors ------------------------------------

export const currentAccount = (state: DefaultRootState): CurrentAccount | undefined => state.user.user?.currentAccount;
export const dynamicPriceEnabled = (state: DefaultRootState): boolean => !!currentAccount(state)?.dynamicPriceEnabled;
export const canExplicitlyChoseCampaignType = (state: DefaultRootState): boolean =>
    !!currentAccount(state)?.canExplicitlyChoseCampaignType;

// ----------------------- /selectors -----------------------------------

// ----------------------- reducer --------------------------------------
interface UserState {
    user: User | null;
    isMultiCabinet: IsMultiCabinet;
    isLoadUser: IsLoad;
    sessionType: SessionType;
    permissions: Permissions[];
    loginData: LoginData;
}

export const USER_INITIAL_STATE: UserState = {
    user: null,
    isMultiCabinet: false,
    isLoadUser: true,
    sessionType: SessionType.Typical,
    permissions: [],
    loginData: {},
};

export default createReducer<UserState, PayloadTypes>(USER_INITIAL_STATE, {
    [SET_USER]: (state, { payload }) => ({
        ...state,
        user: payload,
        isMultiCabinet: !!payload?.accounts?.length,
    }),
    [SET_LOAD_USER]: (state, { payload }) => ({
        ...state,
        isLoadUser: payload,
    }),
    [SET_SESSION_TYPE]: (state, { payload }) => ({
        ...state,
        sessionType: payload,
    }),
    [SET_USER_PERMISSIONS]: (state, { payload }) => ({
        ...state,
        permissions: payload,
    }),
    [SET_LOGIN_DATA]: (state, { payload }) => ({
        ...state,
        loginData: payload,
    }),
});
// ----------------------- /reducer --------------------------------------
